// Preview mode — which mode the whole site is in, for this browser tab.
//
// THE PROBLEM THIS SOLVES
//
// The mode used to live in the URL path (`/test-route-<token>`) and reach
// only App, because only App was given the prop. Every other page was
// therefore Live by construction. Navigating from a test competition to
// My Account silently put the buyer back in Live mode — which is how the
// entry-credit top-up came to ask for live payment credentials that were
// not configured, on a site that was supposed to be in test.
//
// The rule now: if you are in test mode, the WHOLE site is in test mode.
// Same for demo, same for live.
//
// SESSION STORAGE, NOT LOCAL STORAGE
//
// Deliberate. A preview link is a thing you are doing right now, not a
// preference. sessionStorage dies with the tab, so closing it is always a
// way out, and a second tab opened normally is Live — which is what
// someone expects when they deliberately open a fresh window.
//
// THE BANNER IS NOT OPTIONAL
//
// Before this, a visitor on a preview link had NO indication of it — no
// banner, nothing in the header, only the data looking odd. That was
// survivable while the mode ended at the competition page. Once it
// follows you across the whole site it stops being survivable: being
// invisibly stuck in test mode with no way out is worse than the
// inconsistency this replaces. So the banner ships with it, and it
// carries the exit.

const KEY = "589comps_preview_mode";

// The URL prefixes that START a preview session, and the mode each means.
// The backend's requestMode makes the same decision from the token itself;
// this is only about which prefix carried it.
// THE LABEL IS FOR WHOEVER IS LOOKING, NOT FOR US.
//
// These said "Test mode" and "Demo mode" — our own internal vocabulary,
// shown on the PUBLIC site to somebody a partner is demonstrating to.
// They have no reason to know what our modes are called, and the words
// invite the question "as opposed to what?".
//
// What they need to know is that nothing here is real. The mode is still
// carried in `mode` for the colour and for anything that needs to branch
// on it — only the visible words changed.
const PREFIXES = [
  { prefix: "/test-route-", mode: "test", label: "Preview" },
  { prefix: "/demo-route-", mode: "demo", label: "Example site" },
];

// Reads a preview token out of a path, if it starts one.
export function previewFromPath(path) {
  for (const entry of PREFIXES) {
    if (!path.startsWith(entry.prefix)) continue;
    const token = path.slice(entry.prefix.length).split("/")[0];
    if (!token) return null;
    return { token, mode: entry.mode, label: entry.label, prefix: entry.prefix };
  }
  return null;
}

function readStored() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.token || !parsed.mode) return null;
    // THE LABEL IS DERIVED, NEVER READ FROM STORAGE.
    //
    // It used to be stored alongside the token. So when the wording
    // changed on 30 Aug 2026, every tab that had already opened a preview
    // link kept showing the OLD words — through a hard refresh, until the
    // tab itself was closed. The change looked as though it had not
    // deployed.
    //
    // Storage holds what cannot be recomputed: the token, and which mode
    // it is. Anything derivable is derived, so changing it changes what
    // people actually see.
    const entry = PREFIXES.find((e) => e.mode === parsed.mode);
    return { ...parsed, label: entry ? entry.label : parsed.mode };
  } catch (err) {
    // A malformed value must not break the whole app on boot. Treat it as
    // no preview session at all.
    return null;
  }
}

function writeStored(value) {
  try {
    if (value) sessionStorage.setItem(KEY, JSON.stringify(value));
    else sessionStorage.removeItem(KEY);
  } catch (err) {
    // Private browsing, or storage disabled. The site still works; the
    // mode simply will not survive navigation, which is the old
    // behaviour rather than a new failure.
  }
}

// The preview session for this tab, if any.
//
// A token in the PATH always wins and is recorded — that is how a session
// starts, and how following a newer link replaces an older one. Otherwise
// whatever was recorded earlier applies, which is what makes the mode
// follow the visitor to pages that carry no token of their own.
export function currentPreview(path) {
  const fromPath = previewFromPath(path);
  if (fromPath) {
    const stored = readStored();
    if (!stored || stored.token !== fromPath.token) writeStored(fromPath);
    return fromPath;
  }
  return readStored();
}

// Leaves preview mode and returns to the live site.
//
// A full page load rather than a client-side navigate: wsClient holds the
// route token in module state and the config, competition and regions all
// need re-fetching against a different mode. Reloading is one line and
// cannot leave half the app in the old mode; unpicking it in place is
// several careful lines that can.
export function exitPreview() {
  writeStored(null);
  window.location.href = "/";
}
